// src/modules/app-views/_apps/appsindico/parkingSpots/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo } from 'react';
import { Tabs } from 'antd';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import RecordTab from './tabs/RecordTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ParkingSpotsDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = { ...props, data, initTab, viewMode };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const entityId = values?._id || allValues?._id || data?._id;
      await upsert({ dataSet: { ...values }, _id: entityId });
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ParkingSpotsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab {...props} tabName={initTab} />,
    },
  ];
}
